import { type KeyboardEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Icon } from "@legal-500/legal500-design-system";
import { buildFilesUrl } from "../../api/filesUrl";
import {
  powerlistEditions,
  type PreviewEntry,
  type PreviewList,
  type SponsorSummary,
  type SponsorTier,
} from "../../api/powerlist.service";
import { ErrorBanner } from "../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../components/LoadingState";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { useQuery } from "../../hooks/useApi";
import { ROUTES } from "../../routes";
import styles from "./Powerlist.module.css";

function bySortOrder<T extends { sortOrder: number }>(a: T, b: T): number {
  return a.sortOrder - b.sortOrder;
}

const ROW_ONE_TIERS: SponsorTier[] = ["headline_partner", "partnered_with"];

const TIER_LABELS: Record<SponsorTier, (count: number) => string> = {
  headline_partner: () => "Headline partner",
  partnered_with: () => "Partnered with",
  associate_partner: count => (count === 1 ? "Associate partner" : "Associate partners"),
};

function SponsorGroup({ tier, sponsors }: { tier: SponsorTier; sponsors: SponsorSummary[] }) {
  const label = TIER_LABELS[tier](sponsors.length);
  return (
    <div className={styles.sponsorGroup}>
      <p className={styles.muted}>{label}</p>
      <ul className={styles.partners} aria-label={label}>
        {sponsors.map(sponsor => {
          const url = buildFilesUrl(sponsor.logo, sponsor.displayName);
          return (
            <li key={sponsor.id}>
              {url ? (
                <img className={styles.partnerLogo} src={url} alt={sponsor.displayName} />
              ) : (
                <strong>{sponsor.displayName}</strong>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SponsorTiers({ sponsors }: { sponsors: SponsorSummary[] }) {
  const ordered = [...sponsors].sort(bySortOrder);
  const sponsorsIn = (tier: SponsorTier) =>
    ordered.filter(sponsor => (sponsor.tier ?? "partnered_with") === tier);
  const rowOne = ROW_ONE_TIERS.map(tier => ({ tier, sponsors: sponsorsIn(tier) })).filter(
    group => group.sponsors.length > 0,
  );
  const associates = sponsorsIn("associate_partner");
  if (rowOne.length === 0 && associates.length === 0) return null;
  return (
    <div className={styles.sponsorTiers}>
      {rowOne.length > 0 && (
        <div className={styles.sponsorRow}>
          {rowOne.map(group => (
            <SponsorGroup key={group.tier} tier={group.tier} sponsors={group.sponsors} />
          ))}
        </div>
      )}
      {associates.length > 0 && (
        <div className={styles.sponsorRow}>
          <SponsorGroup tier="associate_partner" sponsors={associates} />
        </div>
      )}
    </div>
  );
}

function EntryCard({ entry }: { entry: PreviewEntry }) {
  const photo = buildFilesUrl(entry.photo, entry.name);
  const isDraft = entry.status === "draft";
  return (
    <li className={isDraft ? styles.cardDraft : styles.card} aria-label={entry.name}>
      {photo ? (
        <img className={styles.cardPhoto} src={photo} alt="" />
      ) : (
        <div className={styles.cardPhoto} aria-hidden="true" />
      )}
      <div className={styles.cardBody}>
        <h3 className={styles.cardName}>{entry.name}</h3>
        {entry.jobTitle && <p className={styles.cardLine}>{entry.jobTitle}</p>}
        {entry.companyName && <p className={styles.cardLine}>{entry.companyName}</p>}
        {entry.industrySectorName && <p className={styles.cardLine}>{entry.industrySectorName}</p>}
        <div className={styles.tagList}>
          {isDraft && <span className={`${styles.tag} ${styles.tagWarning}`}>Draft</span>}
          {entry.badges.map(badge => (
            <span key={badge.id} className={styles.tag}>
              {badge.name}
            </span>
          ))}
          {entry.hasInterview && <span className={styles.tag}>Interview</span>}
        </div>
      </div>
    </li>
  );
}

function ListPanel({ list, sponsors }: { list: PreviewList; sponsors: SponsorSummary[] }) {
  const entries = [...list.entries].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
  return (
    <div className={styles.panel}>
      <SponsorTiers sponsors={sponsors} />
      {list.description && <p>{list.description}</p>}
      {entries.length === 0 ? (
        <p className={styles.muted}>No entries in this list yet.</p>
      ) : (
        <ul className={styles.cards} aria-label={`${list.title} entries`}>
          {entries.map(entry => (
            <EntryCard key={entry.id} entry={entry} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default function EditionPreviewPage() {
  const { editionId = "" } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, refetch, isRefetching } = useQuery(
    () => powerlistEditions.preview(editionId),
    [editionId],
  );
  const [activeListId, setActiveListId] = useState<string | null>(null);

  if (loading && data === null) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Preview" />
        <LoadingState message="Loading preview..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Preview" />
        <ErrorBanner
          error={error?.message ?? "Preview unavailable"}
          onRetry={refetch}
          loading={isRefetching}
        />
      </div>
    );
  }

  const lists = [...data.lists].sort(bySortOrder);
  const active = lists.find(list => list.id === activeListId) ?? lists[0];
  const draftCount = lists.reduce(
    (count, list) => count + list.entries.filter(entry => entry.status === "draft").length,
    0,
  );

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const offset = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!offset) return;
    event.preventDefault();
    const next = lists[(index + offset + lists.length) % lists.length];
    setActiveListId(next.id);
    document.getElementById(`powerlist-preview-tab-${next.id}`)?.focus();
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={data.edition.title}
        subtitle={data.regions.map(region => region.displayLabel).join(" · ") || data.series.name}
        meta={<StatusBadge status={data.edition.status} />}
      >
        <Button
          variant="outlined"
          color="default"
          size="small"
          onClick={() => navigate(ROUTES.powerlist.edition(data.edition.id))}
        >
          <Icon icon="arrow-left" size="small" />
          Back to edition
        </Button>
      </PageHeader>
      <p className={styles.previewBanner} role="note">
        Staff preview, not the public site. Draft entries are marked "Draft"
        {draftCount > 0 ? ` (${draftCount} in this edition)` : ""}.
      </p>

      {lists.length === 0 || !active ? (
        <>
          <SponsorTiers sponsors={data.sponsors} />
          <p className={styles.muted}>This edition has no lists yet.</p>
        </>
      ) : (
        <>
          <div className={styles.tabs} role="tablist" aria-label="Lists">
            {lists.map((list, index) => (
              <button
                key={list.id}
                id={`powerlist-preview-tab-${list.id}`}
                type="button"
                role="tab"
                aria-selected={list.id === active.id}
                aria-controls="powerlist-preview-panel"
                tabIndex={list.id === active.id ? 0 : -1}
                className={list.id === active.id ? styles.tabActive : styles.tab}
                onClick={() => setActiveListId(list.id)}
                onKeyDown={event => handleTabKeyDown(event, index)}
              >
                {list.title}
              </button>
            ))}
          </div>
          <div
            id="powerlist-preview-panel"
            role="tabpanel"
            aria-labelledby={`powerlist-preview-tab-${active.id}`}
          >
            <ListPanel
              list={active}
              sponsors={active.sponsors.length > 0 ? active.sponsors : data.sponsors}
            />
          </div>
        </>
      )}
    </div>
  );
}
